/**
 * Admin → Plans tab.
 *
 * Ported from `routes/admin.ts`. Lists active plans with inline create + edit
 * panels. Money fields stay in dollars in the UI; we convert to cents before
 * POST/PATCH to match the API (`setupFeeCents`, `subscriptionCents`).
 */
import { useEffect, useState } from 'hono/jsx';
import { AdminShell } from '../../components/AdminShell';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { Spinner } from '../../components/Spinner';

interface Plan {
  id: string;
  name: string;
  description?: string | null;
  setupFee: number;
  subscription: number;
  subscriptionInterval: string;
  affiliateSetupPct: number;
  affiliateRecurringPct: number;
  isActive: boolean;
  stripeSetupPriceId?: string | null;
  stripeSubscriptionPriceId?: string | null;
}

export function AdminPlans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [editing, setEditing] = useState<Plan | null>(null);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api('/admin/plans');
      if (!r.ok) throw new Error('Failed to load plans');
      const data = await r.json() as { plans?: Plan[] };
      setPlans(data.plans || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditing(null);
    setShowCreate((v) => !v);
  }

  function openEdit(p: Plan) {
    setShowCreate(false);
    setEditing(p);
  }

  async function deletePlan(id: string) {
    if (!confirm('Are you sure you want to delete this plan? This will archive the Stripe product and prices.')) return;
    try {
      const r = await api('/admin/plans/' + id, { method: 'DELETE' });
      const result = await r.json() as { error?: string };
      if (!r.ok) throw new Error(result.error || 'Failed to delete plan');
      showToast('success', 'Plan Deleted', 'Plan has been archived');
      load();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  const activePlans = plans.filter((p) => p.isActive);

  return (
    <AdminShell active="plans">
      <div class="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-slate-100">Pricing Plans</h2>
        <button
          type="button"
          onClick={openCreate}
          class="bg-cyan-500 text-dark-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors"
        >+ Add Plan</button>
      </div>

      {showCreate && (
        <CreatePanel onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      {editing && (
        <EditPanel plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-x-auto">
        {loading ? (
          <div class="p-8 text-center"><Spinner className="mx-auto" /><p class="text-slate-400 mt-4 text-sm">Loading plans...</p></div>
        ) : loadError ? (
          <div class="p-8 text-center text-red-400">{loadError}</div>
        ) : !activePlans.length ? (
          <div class="p-8 text-center text-slate-400">No plans yet. Create one to get started.</div>
        ) : (
          <PlansTable plans={activePlans} onEdit={openEdit} onDelete={deletePlan} />
        )}
      </div>
    </AdminShell>
  );
}

function PlansTable({ plans, onEdit, onDelete }: { plans: Plan[]; onEdit: (p: Plan) => void; onDelete: (id: string) => void }) {
  return (
    <table class="w-full">
      <thead class="bg-dark-700">
        <tr>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Plan</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Setup Fee</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Subscription</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Default Commission</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Stripe</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-700/50">
        {plans.map((p) => {
          const stripeConnected = p.stripeSetupPriceId && p.stripeSubscriptionPriceId;
          return (
            <tr class="hover:bg-dark-700/50">
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-slate-200">{p.name}</div>
                <div class="text-xs text-slate-500">{p.description || ''}</div>
              </td>
              <td class="px-4 py-3 text-sm text-slate-300">${p.setupFee.toLocaleString()}</td>
              <td class="px-4 py-3 text-sm text-slate-300">${p.subscription.toLocaleString()}/{p.subscriptionInterval}</td>
              <td class="px-4 py-3 text-xs text-slate-400">{p.affiliateSetupPct}% setup / {p.affiliateRecurringPct}% recurring</td>
              <td class="px-4 py-3">
                <span class={`text-xs px-2 py-1 rounded ${stripeConnected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {stripeConnected ? 'Connected' : 'Incomplete'}
                </span>
              </td>
              <td class="px-4 py-3 space-x-2">
                <button type="button" onClick={() => onEdit(p)} class="text-cyan-400 hover:text-cyan-300 text-sm">Edit</button>
                <button type="button" onClick={() => onDelete(p.id)} class="text-red-400 hover:text-red-300 text-sm">Delete</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CreatePanel({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [busy, setBusy] = useState<boolean>(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const data = {
      name: fd.get('name'),
      description: (fd.get('description') as string) || undefined,
      setupFeeCents: Math.round(parseFloat((fd.get('setupFee') as string) || '0') * 100),
      subscriptionCents: Math.round(parseFloat((fd.get('subscription') as string) || '0') * 100),
      subscriptionInterval: fd.get('subscriptionInterval'),
      affiliateSetupPct: parseInt((fd.get('affiliateSetupPct') as string) || '0', 10),
      affiliateRecurringPct: parseInt((fd.get('affiliateRecurringPct') as string) || '0', 10),
    };

    setBusy(true);
    try {
      const r = await api('/admin/plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await r.json() as { error?: string; message?: string };
      if (!r.ok) throw new Error(result.error || 'Failed to create plan');
      showToast('success', 'Plan Created', result.message || 'Stripe products created');
      onCreated();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-cyan-500/30 shadow-xl">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Create Plan</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <form onSubmit={onSubmit} class="p-6 space-y-4">
        <div class="grid sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Plan Name</label>
            <input type="text" name="name" required placeholder="e.g. Professional" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Description</label>
            <input type="text" name="description" placeholder="e.g. Full-featured website" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee ($)</label>
            <input type="number" step="0.01" min="0" name="setupFee" required placeholder="1000" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Subscription ($)</label>
            <input type="number" step="0.01" min="0" name="subscription" required placeholder="240" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Billing Interval</label>
          <select name="subscriptionInterval" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
            <option value="day">Daily</option>
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
          </select>
        </div>
        <div class="pt-2 border-t border-slate-700/50">
          <p class="text-xs text-slate-500 mb-3">Default Commission Rates (can be overridden per affiliate)</p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee %</label>
              <input type="number" step="1" min="0" max="100" name="affiliateSetupPct" placeholder="25" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Recurring %</label>
              <input type="number" step="1" min="0" max="100" name="affiliateRecurringPct" placeholder="50" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
          </div>
        </div>
        <p class="text-xs text-slate-500">Stripe products and prices will be created automatically.</p>
        <button type="submit" disabled={busy} class="w-full bg-cyan-500 text-dark-900 py-2 rounded-lg font-medium hover:bg-cyan-400 transition-colors disabled:opacity-60">
          {busy ? 'Creating…' : 'Create Plan'}
        </button>
      </form>
    </div>
  );
}

function EditPanel({ plan, onClose, onSaved }: { plan: Plan; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState<string>(plan.name);
  const [setupFee, setSetupFee] = useState<string>(String(plan.setupFee || 0));
  const [subscription, setSubscription] = useState<string>(String(plan.subscription || 0));
  const [interval, setInterval] = useState<string>(plan.subscriptionInterval || 'year');
  const [affiliateSetupPct, setAffiliateSetupPct] = useState<string>(String(plan.affiliateSetupPct || 0));
  const [affiliateRecurringPct, setAffiliateRecurringPct] = useState<string>(String(plan.affiliateRecurringPct || 0));
  const [busy, setBusy] = useState<boolean>(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const data = {
      name,
      setupFeeCents: Math.round(parseFloat(setupFee || '0') * 100),
      subscriptionCents: Math.round(parseFloat(subscription || '0') * 100),
      subscriptionInterval: interval,
      affiliateSetupPct: parseInt(affiliateSetupPct || '0', 10),
      affiliateRecurringPct: parseInt(affiliateRecurringPct || '0', 10),
    };
    setBusy(true);
    try {
      const r = await api('/admin/plans/' + plan.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to update plan');
      }
      showToast('success', 'Plan Updated', 'Plan settings saved successfully');
      onSaved();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-cyan-500/30 shadow-xl">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Edit Plan</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <form onSubmit={onSubmit} class="p-6 space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Plan Name</label>
          <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee ($)</label>
            <input type="number" step="0.01" min="0" value={setupFee} onInput={(e) => setSetupFee((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Subscription ($)</label>
            <input type="number" step="0.01" min="0" value={subscription} onInput={(e) => setSubscription((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Billing Interval</label>
          <select value={interval} onChange={(e) => setInterval((e.target as HTMLSelectElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
            <option value="day">Daily</option>
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
          </select>
        </div>
        <div class="pt-2 border-t border-slate-700/50">
          <p class="text-xs text-slate-500 mb-3">Default Commission Rates (can be overridden per affiliate)</p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee %</label>
              <input type="number" step="1" min="0" max="100" value={affiliateSetupPct} onInput={(e) => setAffiliateSetupPct((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Recurring %</label>
              <input type="number" step="1" min="0" max="100" value={affiliateRecurringPct} onInput={(e) => setAffiliateRecurringPct((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
          </div>
        </div>
        <button type="submit" disabled={busy} class="w-full bg-cyan-500 text-dark-900 py-2 rounded-lg font-medium hover:bg-cyan-400 transition-colors mt-4 disabled:opacity-60">
          {busy ? 'Saving…' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

export default AdminPlans;
